<script setup lang="ts">
import {
    type SizeConditional,
    useExtractSize,
    type FontSizes,
    type PaletteColorProp,
} from '#imports';
type Sizes = 'small' | 'medium' | 'large' | 'discount';
interface IProps {
    type?: SizeConditional<Sizes>;
    price: number;
    percent?: number;
    prefix?: string;
    free?: boolean;
    sign?: '+' | '-';
    priceColor?: PaletteColorProp | string;
    unitColor?: PaletteColorProp | string;
    unknownPrice?: boolean;
}
const {
    type = 'medium',
    percent = 0,
    prefix = '',
    free,
    sign,
    price,
    unitColor,
    unknownPrice,
    priceColor,
} = defineProps<IProps>();

const toman = 'تومانء';

const { mobile, desktop } = useExtractSize(() => type, 'medium');

const formattedPrice = computed(() =>
    formatMoney(price, {
        // unitClassName: 'toman',
        includeUnit: false,
        free: free && type !== 'discount',
        sign,
    })
);

const prefixSizeConvert: { [Property in Sizes]: FontSizes } = {
    small: 'small',
    medium: 'large',
    large: 'x-large',
    discount: 'x-small',
};

const priceSizeConvert: { [Property in Sizes]: FontSizes } = {
    small: 'x-large',
    medium: 'display4',
    large: 'display3',
    discount: 'x-large',
};

const unitSizeConvert: { [Property in Sizes]: FontSizes } = {
    small: 'medium',
    medium: 'x-large',
    large: 'x-large',
    discount: 'medium',
};
const createPriceSize = (size: Sizes) => ({
    prefix: {
        size: prefixSizeConvert[size],
        weight: '500' as FontWeights,
        color: 'var(--cool-gray-600)',
    },
    price: {
        size: priceSizeConvert[size],
        weight: (size === 'discount' ? '500' : '700') as FontWeights,
        color:
            priceColor ||
            (size === 'discount' ? 'var(--cool-gray-600)' : 'var(--blue-500)'),
        unit: {
            size: unitSizeConvert[size],
            weight: '500' as FontWeights,
            color: unitColor || 'var(--cool-gray-600)',
        },
    },
});

const sizes = computed(() => createPriceSize(desktop.value as Sizes));
const mSizes = computed(() => createPriceSize(mobile.value as Sizes));
</script>
<template>
    <div class="mr-price-root" :class="[desktop, `mobile-${mobile}`]">
        <slot name="prefix">
            <mr-typography
                v-if="prefix"
                :size="{
                    mobile: mSizes.prefix.size,
                    desktop: sizes.prefix.size,
                }"
                :weight="{
                    mobile: mSizes.prefix.weight,
                    desktop: sizes.prefix.weight,
                }"
                :color="sizes.prefix.color"
            >
                {{ prefix }}
            </mr-typography>
        </slot>
        <mr-typography
            v-if="unknownPrice"
            size="large"
            weight="500"
            color="var(--blue-500)"
        >
            نامشخص
        </mr-typography>
        <template v-else>
            <mr-typography
                :size="{
                    mobile: mSizes.price.size,
                    desktop: sizes.price.size,
                }"
                :weight="{
                    mobile: mSizes.price.weight,
                    desktop: sizes.price.weight,
                }"
                :color="sizes.price.color"
                class="price"
            >
                {{ formattedPrice }}
            </mr-typography>
            <mr-typography
                v-if="type !== 'discount' && !free"
                :size="{
                    mobile: mSizes.price.unit.size,
                    desktop: sizes.price.unit.size,
                }"
                :weight="{
                    mobile: mSizes.price.unit.weight,
                    desktop: sizes.price.unit.weight,
                }"
                :color="sizes.price.unit.color"
                class="unit"
            >
                {{ toman }}
            </mr-typography>
            <mr-typography
                v-if="type === 'discount'"
                weight="700"
                color="white"
                class="discount"
            >
                ٪{{ percent }}
            </mr-typography>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.mr-price-root {
    display: flex;
    align-items: center;
    white-space: nowrap;
    @include f-gap(4px);

    .unit {
        margin-right: 6.5px;
    }
    &.discount {
        .price {
            @include tg-l($w5, usePalette($p-cool-gray, $p-600));
            text-decoration: line-through;
        }
        .discount {
            height: 20px;
            background-color: usePalette($p-green, $p-500);
            border-radius: 12px;
            padding: 4px;
            line-height: 16px;
        }
    }
}
</style>
